/**
 * Poster-to-video handoff for the Maston Tri-Fusion perception section.
 *
 * Viewport ≥ 30% starts a 2.0s poster hold while the Shopify CDN video
 * buffers. Playback begins at currentTime 0 only after that hold *and*
 * `canplay`, then native `loop` owns every later cycle. Duration is never
 * hardcoded. Leaving the viewport pauses the element; returning resumes it.
 */
if (!customElements.get('maston-perception-system')) {
  const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';
  const DEFAULT_HOLD_MS = 2000;
  const DEFAULT_THRESHOLD = 0.3;
  const CAN_PLAY_READY_STATE = 3;

  class MastonPerceptionSystem extends HTMLElement {
    #abortController = null;
    #reducedMotionQuery = null;
    #viewportObserver = null;
    #holdTimeout = 0;
    #holdRemaining = DEFAULT_HOLD_MS;
    #holdStartedAt = 0;
    #hasHandedOff = false;
    #isInView = false;
    #isReadyToPlay = false;
    #video = null;
    #copy = null;

    connectedCallback() {
      if (this.#abortController) return;

      this.#video = this.querySelector('[data-perception-video]');
      this.#copy = this.querySelector('[data-perception-copy]');
      this.#holdRemaining = this.#readPositiveNumber('posterHold', DEFAULT_HOLD_MS);
      this.#hasHandedOff = false;
      this.#isReadyToPlay = false;
      this.dataset.mediaState = this.#video ? 'idle' : 'static';

      this.#abortController = new AbortController();
      const { signal } = this.#abortController;

      this.#reducedMotionQuery = window.matchMedia(REDUCED_MOTION_QUERY);
      this.#reducedMotionQuery.addEventListener('change', this.#onReducedMotionChange, { signal });

      if (this.#prefersReducedMotion) {
        this.#revealCopy();
      }

      this.#bindVideo(signal);
      this.#observeViewport();
    }

    disconnectedCallback() {
      this.#abortController?.abort();
      this.#abortController = null;
      this.#clearHoldTimer();
      this.#viewportObserver?.disconnect();
      this.#viewportObserver = null;
      this.#pauseVideo();
      this.#reducedMotionQuery = null;
      this.#video = null;
      this.#copy = null;
    }

    get #prefersReducedMotion() {
      return Boolean(this.#reducedMotionQuery?.matches);
    }

    /**
     * @param {string} name - Dataset key in camelCase.
     * @param {number} fallback - Value used when the attribute is missing.
     * @returns {number}
     */
    #readPositiveNumber(name, fallback) {
      const parsed = Number.parseFloat(this.dataset[name] || '');
      return Number.isFinite(parsed) && parsed >= 0 ? parsed : fallback;
    }

    /**
     * @param {AbortSignal} signal
     */
    #bindVideo(signal) {
      if (!this.#video) return;

      this.#video.muted = true;
      this.#video.defaultMuted = true;
      this.#video.playsInline = true;
      this.#video.loop = true;
      this.#isReadyToPlay = this.#video.readyState >= CAN_PLAY_READY_STATE;

      this.#video.addEventListener('loadedmetadata', this.#onLoadedMetadata, { signal });
      this.#video.addEventListener('canplay', this.#onCanPlay, { signal });
      this.#video.addEventListener('canplaythrough', this.#onCanPlay, { signal });
      this.#video.addEventListener('playing', this.#onPlaying, { signal });
      this.#video.addEventListener('pause', this.#onPause, { signal });
    }

    #observeViewport() {
      const threshold = this.#readPositiveNumber('viewportThreshold', DEFAULT_THRESHOLD);

      this.#viewportObserver = new IntersectionObserver(
        (entries) => {
          const entry = entries.find((item) => item.target === this);
          if (!entry) return;

          const isInView = entry.isIntersecting && entry.intersectionRatio >= threshold;
          if (isInView === this.#isInView) return;

          this.#isInView = isInView;
          if (isInView) this.#onEnterView();
          else this.#onLeaveView();
        },
        { threshold: [0, threshold, 1] },
      );

      this.#viewportObserver.observe(this);
    }

    #onEnterView() {
      this.#revealCopy();

      if (!this.#video || this.#prefersReducedMotion) return;

      if (this.#hasHandedOff) {
        this.#resumeVideo();
        return;
      }

      this.#primeVideo();
      this.#startHoldTimer();
    }

    #onLeaveView() {
      this.#pauseHoldTimer();
      this.#pauseVideo();
    }

    #onReducedMotionChange = () => {
      if (!this.#prefersReducedMotion) return;

      this.#clearHoldTimer();
      this.#pauseVideo();
      this.#hasHandedOff = false;
      this.dataset.mediaState = this.#video ? 'static' : this.dataset.mediaState;
      this.#revealCopy();
    };

    #revealCopy() {
      if (!this.hasAttribute('data-enable-entrance') || !this.#copy) {
        this.toggleAttribute('data-copy-visible', true);
        return;
      }

      this.toggleAttribute('data-copy-visible', true);
    }

    /**
     * Start silent buffering without showing the video layer.
     */
    #primeVideo() {
      if (!this.#video || this.#video.preload === 'auto') return;

      this.#video.preload = 'auto';

      try {
        this.#video.load();
      } catch (error) {
        return;
      }
    }

    #startHoldTimer() {
      if (this.#hasHandedOff || this.#holdTimeout || this.#prefersReducedMotion) return;

      this.dataset.mediaState = 'holding';
      this.#holdStartedAt = performance.now();
      this.#holdTimeout = window.setTimeout(() => {
        this.#holdTimeout = 0;
        this.#holdRemaining = 0;
        this.#tryHandoff();
      }, this.#holdRemaining);
    }

    #pauseHoldTimer() {
      if (!this.#holdTimeout) return;

      const elapsed = performance.now() - this.#holdStartedAt;
      this.#holdRemaining = Math.max(0, this.#holdRemaining - elapsed);
      this.#clearHoldTimer();
    }

    #clearHoldTimer() {
      if (!this.#holdTimeout) return;
      window.clearTimeout(this.#holdTimeout);
      this.#holdTimeout = 0;
    }

    #onLoadedMetadata = () => {
      if (!this.#video) return;

      const duration = this.#video.duration;
      if (Number.isFinite(duration) && duration > 0) {
        this.dataset.mediaDuration = String(duration);
      }
    };

    #onCanPlay = () => {
      this.#isReadyToPlay = true;
      this.#tryHandoff();
    };

    /**
     * Fade the poster only after the hold has elapsed and the stream can play.
     * A rejected `play()` keeps the poster up (iOS Low Power Mode, autoplay blocks).
     */
    #tryHandoff() {
      if (
        this.#hasHandedOff
        || this.#prefersReducedMotion
        || !this.#isInView
        || this.#holdRemaining > 0
        || !this.#video
      ) {
        return;
      }

      if (!this.#isReadyToPlay && this.#video.readyState < CAN_PLAY_READY_STATE) {
        return;
      }

      this.#isReadyToPlay = true;
      this.#video.muted = true;
      this.#video.playsInline = true;

      try {
        this.#video.currentTime = 0;
      } catch (error) {
        // Some engines reject seeking before the first frame; play() still starts at 0.
      }

      const playPromise = this.#video.play();
      if (!(playPromise instanceof Promise)) {
        this.#markPlaying();
        return;
      }

      playPromise.then(this.#markPlaying).catch(this.#lockPoster);
    }

    #resumeVideo() {
      if (!this.#video || this.#prefersReducedMotion || !this.#hasHandedOff) return;

      const playPromise = this.#video.play();
      if (playPromise instanceof Promise) {
        playPromise.catch(() => {
          this.#pauseVideo();
        });
      }
    }

    #pauseVideo() {
      if (!this.#video) return;

      this.#video.pause();
      if (this.#hasHandedOff) this.dataset.mediaState = 'paused';
    }

    #markPlaying = () => {
      if (!this.#isInView || this.#prefersReducedMotion) {
        this.#pauseVideo();
        return;
      }

      this.#hasHandedOff = true;
      this.dataset.mediaState = 'playing';
    };

    #lockPoster = () => {
      this.#hasHandedOff = false;
      this.dataset.mediaState = 'static';
    };

    #onPlaying = () => {
      if (!this.#hasHandedOff || !this.#isInView) return;
      this.dataset.mediaState = 'playing';
    };

    #onPause = () => {
      if (!this.#hasHandedOff || this.#isInView) return;
      this.dataset.mediaState = 'paused';
    };
  }

  customElements.define('maston-perception-system', MastonPerceptionSystem);
}
